import React, { useMemo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useProducts, getProductIcon, ProductPriceLabel } from '../../../hooks/useProducts.jsx';
import { useRefreshHubSessionOnMount } from '../../../hooks/useRefreshHubSessionOnMount.js';
import { getEffectiveLaunchUrl } from '../../../utils/hubEnv.js';
import { useClinicPaymentMethodStatus } from '../../../hooks/useClinicPaymentMethodStatus.js';
import { isProductLicensed, resolveProductLicenseKeys, buildActiveLicenseKeySet, isContractLaunchable, normalizeLicenseKey } from '../../../utils/licenseUtils.js';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';

/**
 * モジュール一覧ページ
 */
const ModulesPage = () => {
  const { user, licenses, isLaunching = false, onLaunchStart } = useOutletContext();
  const canManageLicenses = user?.role === 'doctor' || user?.role === 'admin';
  const { hasPaymentMethod } = useClinicPaymentMethodStatus({ enabled: canManageLicenses });
  useRefreshHubSessionOnMount();
  
  // CMSから製品データを取得
  const { products, loading: productsLoading, error: productsError } = useProducts({ freshOnMount: true });

  const licenseContractMap = new Map();
  if (licenses) {
    const entries = Array.isArray(licenses) ? licenses : Object.values(licenses);
    for (const v of entries) {
      if (!v || v === '') continue;
      const raw = typeof v === 'string' ? v : v?.name;
      const key = normalizeLicenseKey(raw);
      if (key && v?.contract) licenseContractMap.set(key, v.contract);
    }
  }

  const activeLicenseKeys = buildActiveLicenseKeySet(licenses);
  const hasAllLicense = activeLicenseKeys.has('all');

  const isModuleActive = (product) => isProductLicensed(product, activeLicenseKeys, hasAllLicense);

  const getProductContract = (product) => {
    const keys = resolveProductLicenseKeys(product);
    for (const key of keys) {
      const contract = licenseContractMap.get(key);
      if (contract) return contract;
    }
    return undefined;
  };

  const isModuleLaunchable = (product) => {
    const contract = getProductContract(product);
    return isContractLaunchable(contract);
  };

  // アクティブなモジュールと非アクティブなモジュールを分離
  const allActiveModules = products.filter(isModuleActive);
  const launchableModules = allActiveModules.filter(isModuleLaunchable);
  const expiredModules = allActiveModules.filter(m => !isModuleLaunchable(m));
  const availableModules = products.filter(m => !isModuleActive(m));

  const expiredModuleBanner = useMemo(() => {
    if (!canManageLicenses) {
      return {
        message: '試用期間が終了しました。継続利用については医師または管理者にご確認ください。',
        linkTo: '/licenses',
        linkLabel: 'ライセンスを確認',
      };
    }
    if (hasPaymentMethod) {
      return {
        message: '試用期間が終了しました。ライセンス管理ページから再契約してください。',
        linkTo: '/licenses',
        linkLabel: '再契約する',
      };
    }
    return {
      message: '試用期間が終了しました。引き続きご利用いただくには、支払い方法を登録してください。',
      linkTo: '/billing',
      linkLabel: '支払い方法を登録',
    };
  }, [canManageLicenses, hasPaymentMethod]);

  // 起動ボタン（HubLayout の onLaunchStart で遷移アニメーション＋リダイレクト）
  const handleLaunch = (url) => {
    if (!url || url === '#' || (typeof url === 'string' && url.trim() === '')) return;
    if (onLaunchStart) onLaunchStart(url);
  };

  const isLaunchUrlValid = (url) => url && url !== '#' && (typeof url !== 'string' || url.trim() !== '');
  const getLaunchUrl = (product) => getEffectiveLaunchUrl(product) ?? product?.launchUrl ?? '';

  /** パネル数: 利用中1 + 期限切れ（あれば）1 + 追加可能1（あれば）。右下から index 0,1,2 */
  const MODULES_PANEL_TOTAL = 1 + (expiredModules.length > 0 ? 1 : 0) + (availableModules.length > 0 ? 1 : 0);
  const launchWrapperClass = `hub-page-launch-wrapper${isLaunching ? ' is-launching' : ''}`;

  if (productsLoading) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">モジュール</h1>
        </div>
        <div className="hub-main-content hub-enter-panel" data-panel-index="0" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-3xl)' }}>
          <LoadingSpinner size="large" />
        </div>
      </>
    );
  }

  if (productsError) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">モジュール</h1>
        </div>
        <div className="hub-main-content">
          <div className="hub-card hub-enter-panel" data-panel-index="0" style={{ textAlign: 'center', padding: 'var(--hub-space-2xl)' }}>
            <p style={{ color: 'var(--hub-text-secondary)' }}>
              モジュール情報の読み込みに失敗しました。
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={launchWrapperClass}>
      <div className="hub-main-header">
        <h1 className="hub-page-title">モジュール</h1>
      </div>

      <div className="hub-main-content">
        {/* 利用中のモジュール */}
        <div
          className="hub-launch-panel"
          data-panel-index={MODULES_PANEL_TOTAL - 1}
          data-panel-total={MODULES_PANEL_TOTAL}
        >
        <section className="animate-fade-in-up stagger-1" style={{ marginBottom: 'var(--hub-space-3xl)' }}>
          <h2 style={{ 
            fontFamily: 'var(--hub-font-display)',
            fontSize: '1.25rem',
            fontWeight: 700,
            marginBottom: 'var(--hub-space-lg)'
          }}>
            利用中のモジュール
          </h2>

          {launchableModules.length === 0 && expiredModules.length === 0 ? (
            <div className="hub-card" style={{ textAlign: 'center', padding: 'var(--hub-space-2xl)' }}>
              <p style={{ color: 'var(--hub-text-secondary)', margin: 0 }}>
                現在利用中のモジュールはありません。
                下のリストから追加してください。
              </p>
            </div>
          ) : launchableModules.length === 0 ? (
            <div className="hub-card" style={{ textAlign: 'center', padding: 'var(--hub-space-2xl)' }}>
              <p style={{ color: 'var(--hub-text-secondary)', margin: 0 }}>
                起動可能なモジュールはありません。
              </p>
              <Link to="/licenses" className="hub-btn hub-btn-primary" style={{ marginTop: 'var(--hub-space-md)', display: 'inline-block' }}>
                ライセンスを確認
              </Link>
            </div>
          ) : (
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 'var(--hub-space-lg)'
            }}>
              {launchableModules.map((product) => (
                <div
                  key={product.id}
                  className="hub-card hub-card-elevated"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--hub-space-md)',
                    marginBottom: 'var(--hub-space-lg)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--hub-space-sm)',
                        marginBottom: '4px'
                      }}>
                        <h3 style={{ fontWeight: 700, margin: 0 }}>
                          {product.name}
                        </h3>
                        <span style={{
                          background: 'var(--hub-success-light)',
                          color: 'var(--hub-success)',
                          padding: '2px 8px',
                          borderRadius: 'var(--hub-radius-full)',
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap'
                        }}>
                          利用中
                        </span>
                      </div>
                      <p style={{ 
                        color: 'var(--hub-text-secondary)',
                        fontSize: '0.875rem',
                        margin: 0
                      }}>
                        {product.shortDescription}
                      </p>
                    </div>
                  </div>

                  <ul style={{ 
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 var(--hub-space-lg)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--hub-space-xs)'
                  }}>
                    {(product.features || []).slice(0, 3).map((feature, i) => (
                      <li key={i} style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--hub-space-sm)',
                        fontSize: '0.875rem',
                        color: 'var(--hub-text-secondary)'
                      }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16, color: 'var(--hub-success)', flexShrink: 0 }}>
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div style={{ marginTop: 'auto' }}>
                    <button
                      type="button"
                      onClick={() => handleLaunch(getLaunchUrl(product))}
                      className="hub-btn hub-btn-primary"
                      style={{ width: '100%' }}
                      disabled={!isLaunchUrlValid(getLaunchUrl(product))}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                      </svg>
                      起動する
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        </div>

        {/* 試用期間終了モジュール */}
        {expiredModules.length > 0 && (
          <div
            className="hub-launch-panel"
            data-panel-index={availableModules.length > 0 ? 1 : 0}
            data-panel-total={MODULES_PANEL_TOTAL}
          >
          <section className="animate-fade-in-up stagger-2" style={{ marginBottom: 'var(--hub-space-3xl)' }}>
            <h2 style={{ 
              fontFamily: 'var(--hub-font-display)',
              fontSize: '1.25rem',
              fontWeight: 700,
              marginBottom: 'var(--hub-space-lg)',
              color: 'var(--hub-text-secondary)'
            }}>
              試用期間終了
            </h2>

            <div
              className="hub-card"
              style={{
                background: 'rgba(245, 158, 11, 0.08)',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                marginBottom: 'var(--hub-space-lg)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--hub-space-md)'
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="#b45309"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ width: 20, height: 20, flexShrink: 0, overflow: 'visible' }}
              >
                <circle cx="12" cy="12" r="9"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <circle cx="12" cy="16" r="1.1" fill="#b45309" stroke="none"/>
              </svg>
              <div style={{ flex: 1, fontSize: '0.875rem', color: 'var(--hub-text-primary)', lineHeight: 1.5 }}>
                {expiredModuleBanner.message}
              </div>
              <Link
                to={expiredModuleBanner.linkTo}
                className="hub-btn hub-btn-primary"
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {expiredModuleBanner.linkLabel}
              </Link>
            </div>

            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 'var(--hub-space-lg)'
            }}>
              {expiredModules.map((product) => (
                <div
                  key={product.id}
                  className="hub-card"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    opacity: 0.7
                  }}
                >
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--hub-space-md)',
                    marginBottom: 'var(--hub-space-lg)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--hub-space-sm)',
                        marginBottom: '4px'
                      }}>
                        <h3 style={{ fontWeight: 700, margin: 0 }}>
                          {product.name}
                        </h3>
                        <span style={{
                          background: 'rgba(245, 158, 11, 0.15)',
                          color: '#b45309',
                          padding: '2px 8px',
                          borderRadius: 'var(--hub-radius-full)',
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap'
                        }}>
                          試用期間終了
                        </span>
                      </div>
                      <p style={{ 
                        color: 'var(--hub-text-secondary)',
                        fontSize: '0.875rem',
                        margin: 0
                      }}>
                        {product.shortDescription}
                      </p>
                    </div>
                  </div>

                  <div style={{ marginTop: 'auto' }}>
                    <button
                      type="button"
                      className="hub-btn hub-btn-secondary"
                      style={{ width: '100%' }}
                      disabled
                    >
                      起動できません
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
          </div>
        )}

        {/* 追加可能なモジュール（パネル index 0・右下から最初にアニメ） */}
        {availableModules.length > 0 && (
          <div
            className="hub-launch-panel"
            data-panel-index={0}
            data-panel-total={MODULES_PANEL_TOTAL}
          >
          <section className="animate-fade-in-up stagger-2">
            <h2 style={{ 
              fontFamily: 'var(--hub-font-display)',
              fontSize: '1.25rem',
              fontWeight: 700,
              marginBottom: 'var(--hub-space-lg)'
            }}>
              追加可能なモジュール
            </h2>

            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 'var(--hub-space-lg)'
            }}>
              {availableModules.map((product) => (
                <div
                  key={product.id}
                  className="hub-card"
                  style={{
                    opacity: product.isComingSoon ? 0.7 : 0.9,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--hub-space-md)',
                    marginBottom: 'var(--hub-space-lg)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--hub-space-sm)',
                        marginBottom: '4px'
                      }}>
                        <h3 style={{ fontWeight: 700, margin: 0 }}>
                          {product.name}
                        </h3>
                        {product.isComingSoon && (
                          <span className="hub-badge hub-badge-warning">準備中</span>
                        )}
                      </div>
                      <p style={{ 
                        color: 'var(--hub-text-secondary)',
                        fontSize: '0.875rem',
                        margin: 0
                      }}>
                        {product.shortDescription}
                      </p>
                    </div>
                  </div>

                  {/* 価格表示 */}
                  <div style={{
                    padding: 'var(--hub-space-sm) var(--hub-space-md)',
                    background: 'var(--hub-bg-secondary)',
                    borderRadius: 'var(--hub-radius-md)',
                    marginBottom: 'var(--hub-space-md)',
                    textAlign: 'center'
                  }}>
                    <span style={{
                      fontWeight: 700,
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                      color: product.isFree ? 'var(--hub-success)' : 'var(--hub-text-primary)'
                    }}>
                      <ProductPriceLabel product={product} />
                    </span>
                  </div>

                  <ul style={{ 
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 var(--hub-space-lg)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--hub-space-xs)'
                  }}>
                    {(product.features || []).slice(0, 3).map((feature, i) => (
                      <li key={i} style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--hub-space-sm)',
                        fontSize: '0.875rem',
                        color: 'var(--hub-text-muted)'
                      }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16, flexShrink: 0 }}>
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div style={{ marginTop: 'auto' }}>
                    {product.isComingSoon ? (
                      <button 
                        className="hub-btn hub-btn-secondary"
                        style={{ width: '100%' }}
                        disabled
                      >
                        Coming Soon
                      </button>
                    ) : (
                      <Link 
                        to="/licenses"
                        className="hub-btn hub-btn-secondary"
                        style={{ width: '100%' }}
                      >
                        ライセンスを追加
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default ModulesPage;
